<template>
  <div>
    <v-data-table
        v-model="val"
        v-bind="$attrs"
        :items="filteredItems"
        :headers="headers"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        :search="search"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        @click:row="navigateToItem"
        @page-count="pageCount = $event"
        hide-default-footer
        :show-select="showSelect"
        v-on="$listeners"
    >
      <template v-for="(_, slot) of $scopedSlots" v-slot:[slot]="scope"
      ><slot :name="slot" v-bind="scope"
      /></template>
      <template v-slot:top>
        <div>
          <v-row align="baseline" justify="space-between" class="px-3">
            <v-col cols="3">
              <v-row align="baseline">
                {{ i18n.t("_RowsPerPage") }}:
                <v-select
                    class="mx-4"
                    v-model="itemsPerPage"
                    :items="[5, 10, 20, 50]"
                    style="max-width: 50px"
                ></v-select>
              </v-row>
            </v-col>
            <v-col v-if="showRefresh" cols="1">
              <v-btn icon @click="$emit('canRefresh')">
                <v-icon color="primary">mdi-refresh</v-icon>
              </v-btn></v-col
            >
            <v-col :cols="showRefresh ? '3' : '4'">
              <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  :label="i18n.t('_Search')"
                  single-line
                  hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="5">
              <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </v-col>
          </v-row>
          <v-divider></v-divider>
        </div>
      </template>
      <template v-for="h in headers" v-slot:[`header.${h.value}`]="{ header }">
        {{ header.text }}
        <v-menu
            offset-y
            :close-on-content-click="false"
            dense
            v-if="h.columnFilter"
            :key="h.value"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon small> mdi-filter </v-icon>
            </v-btn>
          </template>
          <div style="background-color: white; width: 200px" class="pa-3">
            <v-checkbox
                v-for="val in extractUniqueValue(h.value)"
                :key="val"
                dense
                v-model="columnFilters"
                :value="`${header.value}.${val}`"
                :label="val"
                class="ma-0"
            ></v-checkbox>
            <v-btn
                @click="columnFilters = []"
                small
                text
                color="primary"
                class="ml-2 mb-2"
            >{{ i18n.t("_FilterReset") }}</v-btn
            >
          </div>
        </v-menu>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn
            v-if="path !== undefined"
            icon
            color="primary"
            @click.stop.prevent="navigateToItem(item)"
            v-bind="item.props"
        >
          <v-icon>mdi-launch</v-icon>
        </v-btn>
        <v-btn
            v-if="item.checkable"
            icon
            color="grey darken-1"
            @click.stop.prevent="check(item)"
            v-bind="item.props"
        >
          <v-icon>mdi-check-circle-outline</v-icon>
        </v-btn>
        <v-btn
            v-if="item.editable"
            icon
            color="grey darken-1"
            @click.stop.prevent="edit(item)"
            v-bind="item.props"
        >
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
        <v-btn
            v-if="item.removable"
            icon
            color="grey darken-1"
            @click.stop.prevent="remove(item)"
            v-bind="item.props"
            :data-cy="'btn-delete-' + item[itemName]"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>

        <v-btn
            v-if="item.unsubscribable"
            icon
            color="grey darken-1"
            @click.stop.prevent="unsubscribe(item)"
            v-bind="item.props"
        >
          <v-icon>mdi-cancel</v-icon>
        </v-btn>
      </template>
      <template v-slot:footer>
        <v-divider></v-divider>
        <div class="my-4"></div>
        <div>
          <v-row align="baseline" justify="space-between" class="px-4">
            <v-col col="2">
              <v-row align="baseline">{{ leftBottomInfo }}</v-row>
            </v-col>
            <v-col cols="5">
              <v-pagination v-model="page" :length="pageCount"></v-pagination>
            </v-col>
          </v-row>
        </div>
      </template>
    </v-data-table>
    <v-input :value="val" :rules="rules"></v-input>
    <v-dialog v-if="showDialog" v-model="showDialog" :width="dialogWidth">
      <v-card>
        <slot :name="dialogName" :item="item">
          <slot :name="dialogName + '-title'" :item="item">
            <v-card-title class="headline background">
              {{ dialogTitle }}</v-card-title
            >
          </slot>
          <v-divider></v-divider>
          <v-layout column justify-center align-center>
            <v-card-text class="px-6">
              <slot :name="dialogName + '-content'" :item="item">
                <p v-if="dialogName === 'remove-dialog'">
                  {{ i18n.t("__CFA1_DeleteItem") }}
                </p>
              </slot>
            </v-card-text>
          </v-layout>

          <v-divider></v-divider>
          <v-card-actions class="px-6 py-4">
            <slot :name="dialogName + '-actions'" :item="item">
              <slot :name="dialogName + '-actions-left'">
                <v-btn @click="cancelDialog">{{ i18n.t("_Cancel") }}</v-btn>
              </slot>
              <v-spacer></v-spacer>
              <slot
                  :name="dialogName + '-actions-right'"
                  :item="item"
                  :dialog="showDialog"
              >
                <v-btn
                    v-if="dialogName === 'check-dialog'"
                    @click="
                    $emit('canCheck', item);
                    showDialog = false;
                  "
                    color="primary"
                >{{ i18n.t("_Acknowledge") }}</v-btn
                >
                <v-btn
                    v-if="dialogName === 'edit-dialog'"
                    @click="
                    $emit('canEdit', item);
                    showDialog = false;
                  "
                    color="primary"
                >{{ i18n.t("_Save") }}</v-btn
                >
                <v-btn
                    v-if="dialogName === 'remove-dialog'"
                    @click="
                    $emit('canRemove', item);
                    showDialog = false;
                  "
                    color="error"
                    :data-cy="'btn-confirm-delete-' + item[itemName]"
                >{{ i18n.t("_Remove") }}</v-btn
                >
                <v-btn
                    v-if="dialogName === 'unsubscribe-dialog'"
                    @click="
                    $emit('canUnsubscribe', item);
                    showDialog = false;
                  "
                    color="error"
                >{{ i18n.t("_Unsubscribe") }}</v-btn
                >
              </slot>
            </slot>
          </v-card-actions>
        </slot>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import i18n from "../helper/i18n"

export default {
  mounted() {
    this.sortBy = localStorage.getItem("sortBy");
    this.sortDesc = localStorage.getItem("sortDesc");
  },
  data() {
    return {
      sortBy: '',
      sortDesc: '',
      search: "",
      hasNavigation: false,
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      columnFilters: [],
      showDialog: false,
      dialogName: "",
      dialogTitle: "",
      item: undefined,
      i18n: i18n
    };
  },
  props: {
    value: {
      type: Array,
    },
    items: {
      type: Array,
      required: true,
    },
    headers: {
      type: Array,
    },
    path: {
      type: String,
    },
    showSelect: {
      type: Boolean,
    },
    showRefresh: {
      type: Boolean,
      default: false,
    },
    dialogWidth: {
      type: String,
      default: "550",
    },
    itemName: {
      type: String,
      default: "name",
    },
    rules: {
      type: Array,
    },
  },
  methods: {
    navigateToItem(item) {
      if (this.path !== undefined) {
        this.$router.push(this.path + item.id);
      }
    },
    unsubscribe(item) {
      this.showDialog = true;
      this.dialogName = "unsubscribe-dialog";
      this.dialogTitle = this.i18n.t("_UnsubscribeItem", {
        item: "'" + item[this.itemName] + "'",
      });
      this.dialogTitle =
          this.dialogTitle.charAt(0).toUpperCase() + this.dialogTitle.slice(1);
      this.item = Object.assign({}, item);
    },
    check(item) {
      this.showDialog = true;
      this.dialogName = "check-dialog";
      this.dialogTitle = this.i18n.t("_CheckItem", {
        item: "'" + item[this.itemName] + "'",
      });
      this.dialogTitle =
          this.dialogTitle.charAt(0).toUpperCase() + this.dialogTitle.slice(1);
      this.item = Object.assign({}, item);
    },
    edit(item) {
      this.showDialog = true;
      this.dialogName = "edit-dialog";
      this.dialogTitle = this.i18n.t("_EditItem", {
        item: "'" + item[this.itemName] + "'",
      });
      this.dialogTitle =
          this.dialogTitle.charAt(0).toUpperCase() + this.dialogTitle.slice(1);
      this.item = Object.assign({}, item);
    },
    remove(item) {
      this.showDialog = true;
      this.dialogName = "remove-dialog";
      this.dialogTitle = this.i18n.t("_RemoveItem", {
        item: "'" + item[this.itemName] + "'",
      });
      this.dialogTitle =
          this.dialogTitle.charAt(0).toUpperCase() + this.dialogTitle.slice(1);
      this.item = Object.assign({}, item);
    },
    cancelDialog() {
      this.showDialog = false;
    },

    extractUniqueValue(column, withColumnPrefix) {
      return this.items
          .map((i) => (withColumnPrefix ? column + "." + i[column] : i[column]))
          .filter((value, index, self) => {
            //filter only unique values
            return self.indexOf(value) === index;
          });
    },
  },
  computed: {
    val: {
      get: function () {
        return this.value;
      },
      set: function (newValue) {
        this.$emit("input", newValue);
      },
    },
    lowerEntryIndex() {
      if (this.itemsLength > 0) {
        return (this.page - 1) * this.itemsPerPage + 1;
      } else {
        return 0;
      }
    },
    upperEntryIndex() {
      return Math.min(this.itemsLength, this.page * this.itemsPerPage);
    },

    itemsLength() {
      return this.items.length;
    },
    // TODO not perfectly correct during search
    leftBottomInfo() {
      let a = this.lowerEntryIndex;
      let b = this.upperEntryIndex;
      let c = this.itemsLength;

      return this.i18n.t("_TableFooter")
          .replace("${a}", a)
          .replace("${b}", b)
          .replace("${c}", c);
    },
    filteredItems() {
      let filterGroups = this.columnFilters.reduce((group, filter) => {
        let fields = filter.split(".");
        const columnName = fields[0];
        group[columnName] = group[columnName] || [];
        group[columnName].push(fields[1]);
        return group;
      }, {});

      return this.items.filter((item) => {
        let columns = Object.keys(filterGroups);
        for (let i = 0; i < columns.length; i++) {
          let columnName = columns[i];
          if (!filterGroups[columnName].includes(item[columnName]))
            return false;
        }
        return true;
      });
    },
  },
  watch: {
    value: function () {
      this.$emit("input", this.value);
    },
    items: function () {
      this.columnFilters = [];
      for (const i in this.headers) {
        let header = this.headers[i];
        if (header.columnFilter) {
          this.columnFilters.push(
              ...this.extractUniqueValue(header.value, true)
          );
          this.columnFilters.push(header.value + ". ");
        }
      }
    },
    sortBy: function (newValue) {
      localStorage.setItem('sortBy', newValue);
    },
    sortDesc: function (newValue) {
      localStorage.setItem('sortDesc', newValue);
    }
  },
};
</script>
